/**
 * useTripSegmentElevation — shared hook used by `TripOverviewMap` and
 * `RouteMapViewer` to poll the backend elevation endpoint.
 *
 * b99.10.04 (2026-05-19) — Phase 1 of the map-component unification.
 * Centralises the polling logic that was duplicated in two places.
 * Future improvements (cooldown, retry-after, failure UI) flow into
 * both views automatically.
 *
 * Returns:
 *   { coordinates, status, ready, pending, failed, attempt }
 *
 * - `coordinates`: array of {lat, lng, alt, t} once `ready`. Empty
 *   otherwise.
 * - `status`: "ready" | "pending" | "failed" | "no_source" | null
 *   (null = before the first response).
 * - Convenience booleans `ready`/`pending`/`failed` mirror `status`.
 * - `attempt`: which poll iteration we're on (0-indexed). Useful for
 *   UI to surface "ainda a calcular…" copy after the first attempt.
 *
 * The hook stops polling when status leaves "pending". Falls back to
 * a 5-step exponential backoff (2.5s → 4 → 6 → 10 → 15s), then gives
 * up. The backend persists the result so the next page-view is
 * instant.
 */
import { useEffect, useState } from "react";
import { BACKEND_URL } from "../../config";

const POLL_DELAYS_MS = [2500, 4000, 6000, 10000, 15000];

export function useTripSegmentElevation({
  tripId,
  segmentIndex,
  // Optional skip: when the parent already has elevation coords (e.g.
  // passed as a prop) it can pass `skip=true` to avoid a duplicate
  // fetch. Mirrors the existing `elevationCoords` shortcut in
  // RouteMapViewer.
  skip = false,
  // Bumping this re-runs the fetch (used after a manual altitude recompute).
  refreshKey = 0,
} = {}) {
  const [coordinates, setCoordinates] = useState([]);
  const [status, setStatus] = useState(null);
  const [reason, setReason] = useState(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (skip) return;
    if (!tripId || segmentIndex === null || segmentIndex === undefined) return;
    let cancelled = false;
    let timer = null;

    const fetchOnce = async () => {
      try {
        const res = await fetch(
          `${BACKEND_URL}/api/trips/${tripId}/segments/${segmentIndex}/elevation`
        );
        if (!res.ok) return { done: true };
        const data = await res.json();
        if (cancelled) return { done: true };
        const resolvedStatus = data.status
          || (data.has_altitude ? "ready" : (data.pending ? "pending" : "no_source"));
        setStatus(resolvedStatus);
        setReason(data.reason || null);
        if (resolvedStatus === "ready"
            && Array.isArray(data.coordinates)
            && data.has_altitude) {
          setCoordinates(data.coordinates);
          return { done: true };
        }
        return { done: resolvedStatus !== "pending" };
      } catch (_e) {
        return { done: true };
      }
    };

    (async () => {
      let r = await fetchOnce();
      let n = 0;
      while (!r.done && n < POLL_DELAYS_MS.length && !cancelled) {
        await new Promise((resolve) => {
          timer = setTimeout(resolve, POLL_DELAYS_MS[n]);
        });
        n++;
        setAttempt(n);
        if (cancelled) return;
        r = await fetchOnce();
      }
    })();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [tripId, segmentIndex, skip]);

  return {
    coordinates,
    status,
    reason,
    ready: status === "ready",
    pending: status === "pending",
    failed: status === "failed",
    attempt,
  };
}

/**
 * Multi-segment variant used by `TripOverviewMap` to fetch elevation
 * for several segments in parallel. Returns a `Record<segmentIdx, coords[]>`
 * and an overall `anyPending` flag (so the polling can stop globally
 * when no segment is still pending).
 *
 * `segments` here is an array of objects `{ index, segment_id }` — the
 * caller is responsible for translating its visible/filtered indices
 * to the original `trip.segments` index used by the backend route.
 */
export function useTripAllSegmentsElevation({
  tripId,
  segments = [],
  // Re-trigger key — when the array reference changes the hook re-fetches.
  // Pass a stable serialised form when needed.
  resetKey = "",
}) {
  const [elevationsBySegment, setElevationsBySegment] = useState({});
  const [statusBySegment, setStatusBySegment] = useState({});

  useEffect(() => {
    if (!tripId || segments.length === 0) return;
    let cancelled = false;
    let timer = null;

    const fetchAll = async () => {
      const results = {};
      const statuses = {};
      await Promise.all(segments.map(async ({ key, index }) => {
        try {
          const res = await fetch(
            `${BACKEND_URL}/api/trips/${tripId}/segments/${index}/elevation`
          );
          if (!res.ok) return;
          const data = await res.json();
          statuses[key] = data.status
            || (data.has_altitude ? "ready" : (data.pending ? "pending" : "no_source"));
          if (Array.isArray(data.coordinates) && data.has_altitude) {
            results[key] = data.coordinates;
          }
        } catch (_e) { /* silent */ }
      }));
      if (cancelled) return false;
      setElevationsBySegment(results);
      setStatusBySegment(statuses);
      return Object.values(statuses).some((s) => s === "pending");
    };

    (async () => {
      let stillPending = await fetchAll();
      let n = 0;
      while (stillPending && n < POLL_DELAYS_MS.length && !cancelled) {
        await new Promise((resolve) => {
          timer = setTimeout(resolve, POLL_DELAYS_MS[n]);
        });
        n++;
        if (cancelled) return;
        stillPending = await fetchAll();
      }
    })();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tripId, resetKey]);

  return { elevationsBySegment, statusBySegment };
}
